import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { SiilaTranslationService } from "../../core/services/siila-translation.service";
import {
  clearPrerenderRedirect,
  emitPrerenderRedirect
} from "../utils/prerender";

/**
 * Points an id-driven page at its ONE canonical URL.
 *
 * Routes such as `solutions/:id/:name`, `products/:id/:name` and
 * `resource/tags/:name/:id/:color` resolve entirely from the id — the name
 * segment is decoration the router never reads. Left alone, every spelling of
 * that segment is a live, indexable page that declares itself canonical, and a
 * URL carrying another language's name renders content that disagrees with it.
 *
 * Callers compute the canonical path from the entity data and hand it here.
 * Visitors get the address bar corrected in place (`replaceState` — the page
 * already shows the right content, so a reload would buy nothing); crawlers get
 * a real 301 through the Prerender.io meta pair, which is the only way to answer
 * with an HTTP status in a deployment that has no SSR.
 *
 * The tags on the crawler path are emitted unconditionally rather than behind a
 * bot check: Prerender.io forwards the crawler's own user agent here, so the
 * page cannot tell a renderer from a person, and the tags are inert in a browser.
 * See `shared/utils/prerender.ts`.
 */
@Injectable()
export class UrlCanonicalService {
  /**
   * The canonical URL this service last redirected TO. After the address bar is
   * corrected the path is canonical, so a second `apply` for the same page (tag
   * pages canonicalize from two data sources, whichever resolves first) would
   * otherwise clear the very 301 the crawler still needs.
   */
  private lastEmittedUrl: string = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    private siilaTranslationService: SiilaTranslationService
  ) {}

  /**
   * Bring the current page to `canonicalPath` and return the absolute canonical
   * URL, ready to hand to `MetaTagService.addCanonicalTag`. A no-op returning the
   * path unchanged during SSR.
   *
   * `canonicalPath` is written to history RAW (spaces and `&` included, as the
   * existing link builders emit them) so URL formatting does not change; only the
   * `Location` value is percent-encoded, since that one becomes an HTTP header.
   */
  apply(canonicalPath: string): string {
    if (!canonicalPath || !isPlatformBrowser(this.platformId)) {
      return canonicalPath;
    }

    const search = window.location.search || "";
    const canonicalUrl = `${window.location.origin}${canonicalPath}${search}`;

    if (this.isSamePath(window.location.pathname, canonicalPath)) {
      // Never leave a PREVIOUS page's redirect behind on a URL that is correct —
      // but keep the one we just emitted to reach this very URL.
      if (this.lastEmittedUrl !== canonicalUrl) {
        clearPrerenderRedirect(this.document);
        this.lastEmittedUrl = null;
      }
      return canonicalUrl;
    }

    emitPrerenderRedirect(this.document, encodeURI(canonicalUrl));
    this.lastEmittedUrl = canonicalUrl;
    this.siilaTranslationService.replacePathKeepingQuery(canonicalPath);
    return canonicalUrl;
  }

  /**
   * Compare the live path with the canonical one, ignoring only what a URL picks
   * up in transit: percent-encoding (`Mexico%20City`) and a trailing slash. Case
   * is significant — these names are canonical as the CMS spells them.
   */
  private isSamePath(currentPath: string, canonicalPath: string): boolean {
    return this.normalize(currentPath) === this.normalize(canonicalPath);
  }

  private normalize(path: string): string {
    let decoded = path || "";
    try {
      decoded = decodeURIComponent(decoded);
    } catch (e) {}
    return decoded.replace(/\/+$/, "");
  }
}
